/**
 * Admin — Contratos do curso (Secção 10).
 *
 * Gestão dos contratos de formação:
 *   - 10.1 Contratos Formadores (gerar + upload assinado + download)
 *   - 10.2 Contrato Empresa (dados empresa + gerar + upload assinado)
 *   - 10.3 Contratos Formandos (link para pasta com contratos assinados individualmente)
 */
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  FileText, Loader2, Save, Download, Upload, RefreshCcw, ExternalLink,
  Building2, UserCog, Users,
} from "lucide-react";
import { adminApi } from "../api";
import { toast } from "sonner";
import CursoTabsNav from "../components/CursoTabsNav";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminCursoContratos() {
  const [params] = useSearchParams();
  const cursoId = params.get("curso") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [emp, setEmp] = useState({
    nome: "", nif: "", sede: "", representante: "", representante_qualidade: "",
  });

  const load = useCallback(async () => {
    if (!cursoId) return;
    setLoading(true);
    try {
      const r = await adminApi.contratosSnapshot(cursoId);
      setData(r);
      setEmp({
        nome: r.empresa.nome || "", nif: r.empresa.nif || "",
        sede: r.empresa.sede || "", representante: r.empresa.representante || "",
        representante_qualidade: r.empresa.representante_qualidade || "",
      });
    } catch (e) { toast.error(e.message || "Erro"); }
    finally { setLoading(false); }
  }, [cursoId]);

  useEffect(() => { load(); }, [load]);

  const saveEmpDados = async () => {
    setBusy("emp-save");
    try {
      await adminApi.contratosEmpresaDadosPatch(cursoId, emp);
      toast.success("Dados da empresa guardados");
      await load();
    } catch (e) { toast.error(e.message || "Erro"); }
    finally { setBusy(""); }
  };

  const gerarEmp = async () => {
    if (!emp.nome.trim()) { toast.error("Preenche pelo menos o nome da empresa"); return; }
    setBusy("emp-gen");
    try {
      const r = await adminApi.contratosEmpresaGerar(cursoId);
      toast.success(`Contrato gerado (${(r.size/1024).toFixed(0)} KB)`);
      await load();
    } catch (e) { toast.error(e.message || "Erro"); }
    finally { setBusy(""); }
  };

  const gerarFormador = async (fid, nome) => {
    setBusy(`f-gen-${fid}`);
    try {
      const r = await adminApi.contratosFormadorGerar(cursoId, fid);
      toast.success(`Contrato gerado para ${nome}`);
      await load();
    } catch (e) { toast.error(e.message || "Erro"); }
    finally { setBusy(""); }
  };

  const uploadEmpresa = async (file) => {
    if (!file) return;
    setBusy("emp-upload");
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await fetch(`${API}/api/admin/cursos/${cursoId}/contratos/empresa/upload`,
                             { method: "POST", credentials: "include", body: fd });
      if (!r.ok) throw new Error((await r.json()).detail || "Falha upload");
      toast.success("Contrato assinado carregado.");
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(""); }
  };

  const uploadFormador = async (fid, file) => {
    if (!file) return;
    setBusy(`f-upload-${fid}`);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await fetch(`${API}/api/admin/cursos/${cursoId}/contratos/formadores/${fid}/upload`,
                             { method: "POST", credentials: "include", body: fd });
      if (!r.ok) throw new Error((await r.json()).detail || "Falha upload");
      toast.success("Contrato assinado carregado.");
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(""); }
  };

  const openDownload = (url) => window.open(`${API}${url}`, "_blank");

  if (!cursoId) {
    return <div className="p-6 text-[#6E6754]">Falta o parâmetro <code>?curso=&lt;ID&gt;</code>.</div>;
  }

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="admin-curso-contratos-page">
      <CursoTabsNav cursoId={cursoId} current="contratos" />
      <header className="mb-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#F3F0E7] grid place-items-center">
          <FileText className="w-5 h-5 text-[#A8843C]" />
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-semibold text-[#1B1B1B]">Contratos do Curso #{cursoId} <span className="text-xs text-[#978D78] ml-2">(Secção 10 do DTP)</span></h1>
        </div>
        <button onClick={load} className="text-xs text-[#6B7280] hover:text-[#0F1829] inline-flex items-center gap-1">
          <RefreshCcw size={12} /> Recarregar
        </button>
      </header>

      {loading || !data ? (
        <div className="p-6 flex items-center gap-2 text-[#6E6754]"><Loader2 className="animate-spin" size={16}/> A carregar…</div>
      ) : (
        <div className="space-y-5">
          {/* 10.1 Formadores */}
          <section className="bg-white border border-[#E6E1D6] rounded-md p-4" data-testid="sec-formadores">
            <h2 className="text-sm font-bold uppercase tracking-[0.12em] mb-3 flex items-center gap-2">
              <UserCog size={14} /> 10.1 Contrato Formador
            </h2>
            {data.formadores.length === 0 ? (
              <p className="text-xs text-[#978D78] italic">
                Sem formadores. Adiciona em <a href={`/admin/cursos/formadores?curso=${cursoId}`} className="text-[#A8843C] font-semibold hover:underline">Formadores do curso ↗</a>.
              </p>
            ) : (
              <ul className="space-y-2">
                {data.formadores.map((f) => (
                  <li key={f.id} className="border border-[#E6E1D6] rounded p-2.5 flex items-center gap-3" data-testid={`row-f-${f.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#0F1829]">{f.nome}</p>
                      <p className="text-[10px] text-[#978D78]">
                        NIF: {f.nif || "—"} · IBAN: <span className="font-mono">{f.iban || "— (define em Manutenção Formadores)"}</span>
                      </p>
                    </div>
                    <button onClick={() => gerarFormador(f.id, f.nome)} disabled={busy === `f-gen-${f.id}`}
                            className="text-[10px] px-2 py-1 rounded bg-[#0F1829] text-white hover:bg-[#1A2540] disabled:opacity-50 inline-flex items-center gap-1"
                            data-testid={`btn-gen-${f.id}`}>
                      {busy === `f-gen-${f.id}` ? <Loader2 className="animate-spin" size={10}/> : <FileText size={10}/>} Gerar
                    </button>
                    {f.contrato_gerado_path && (
                      <button onClick={() => openDownload(`/api/admin/cursos/${cursoId}/contratos/formadores/${f.id}/download/gerado`)}
                              className="text-[10px] px-2 py-1 rounded border border-[#E6E1D6] hover:bg-[#FAF8F2] inline-flex items-center gap-1"
                              data-testid={`btn-view-gen-${f.id}`}>
                        <Download size={10}/> Gerado
                      </button>
                    )}
                    <label className="text-[10px] px-2 py-1 rounded border border-[#E6E1D6] hover:bg-[#FAF8F2] cursor-pointer inline-flex items-center gap-1">
                      <Upload size={10}/> {busy === `f-upload-${f.id}` ? "..." : "Assinado"}
                      <input type="file" accept="application/pdf" className="hidden"
                             onChange={(e) => uploadFormador(f.id, e.target.files?.[0])} />
                    </label>
                    {f.contrato_assinado_path && (
                      <button onClick={() => openDownload(`/api/admin/cursos/${cursoId}/contratos/formadores/${f.id}/download/assinado`)}
                              className="text-[10px] px-2 py-1 rounded bg-[#E6F4EA] text-[#0A6B2E] hover:bg-[#C6E6D0] inline-flex items-center gap-1"
                              data-testid={`btn-view-signed-${f.id}`}>
                        <Download size={10}/> Ver Assinado
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* 10.2 Empresa */}
          <section className="bg-white border border-[#E6E1D6] rounded-md p-4" data-testid="sec-empresa">
            <h2 className="text-sm font-bold uppercase tracking-[0.12em] mb-3 flex items-center gap-2">
              <Building2 size={14} /> 10.2 Contrato Empresa
            </h2>
            <div className="grid grid-cols-2 gap-3 mb-3">
              {[
                ["nome", "Nome da Empresa"], ["nif", "NIF"], ["sede", "Sede"],
                ["representante", "Representante"], ["representante_qualidade", "Qualidade (Diretor, Sócio Gerente, etc)"],
              ].map(([key, label]) => (
                <label key={key} className="text-xs">
                  <span className="block text-[#6E6754] font-semibold mb-1">{label}</span>
                  <input value={emp[key]} onChange={(e) => setEmp({ ...emp, [key]: e.target.value })}
                         className="w-full px-2 py-1.5 text-sm border border-[#E6E1D6] rounded focus:border-[#A8843C] focus:outline-none"
                         data-testid={`input-emp-${key}`} />
                </label>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={saveEmpDados} disabled={busy === "emp-save"}
                      className="text-xs px-3 py-1.5 bg-[#A8843C] text-white rounded hover:bg-[#8f6e2f] disabled:opacity-50 inline-flex items-center gap-1.5"
                      data-testid="btn-emp-save">
                {busy === "emp-save" ? <Loader2 className="animate-spin" size={12}/> : <Save size={12}/>} Guardar dados
              </button>
              <button onClick={gerarEmp} disabled={busy === "emp-gen"}
                      className="text-xs px-3 py-1.5 bg-[#0F1829] text-white rounded hover:bg-[#1A2540] disabled:opacity-50 inline-flex items-center gap-1.5"
                      data-testid="btn-emp-gen">
                {busy === "emp-gen" ? <Loader2 className="animate-spin" size={12}/> : <FileText size={12}/>} Gerar contrato PDF
              </button>
              {data.empresa.contrato_gerado_path && (
                <button onClick={() => openDownload(`/api/admin/cursos/${cursoId}/contratos/empresa/download/gerado`)}
                        className="text-xs px-3 py-1.5 border border-[#E6E1D6] rounded hover:bg-[#FAF8F2] inline-flex items-center gap-1">
                  <Download size={12}/> Ver gerado
                </button>
              )}
              <label className="text-xs px-3 py-1.5 border border-[#E6E1D6] rounded hover:bg-[#FAF8F2] inline-flex items-center gap-1.5 cursor-pointer">
                <Upload size={12}/> {busy === "emp-upload" ? "A enviar…" : "Upload assinado"}
                <input type="file" accept="application/pdf" className="hidden"
                       onChange={(e) => uploadEmpresa(e.target.files?.[0])} />
              </label>
              {data.empresa.contrato_assinado_path && (
                <button onClick={() => openDownload(`/api/admin/cursos/${cursoId}/contratos/empresa/download/assinado`)}
                        className="text-xs px-3 py-1.5 bg-[#E6F4EA] text-[#0A6B2E] rounded hover:bg-[#C6E6D0] inline-flex items-center gap-1.5">
                  <Download size={12}/> Ver assinado
                </button>
              )}
            </div>
          </section>

          {/* 10.3 Formandos (readonly link) */}
          <section className="bg-white border border-[#E6E1D6] rounded-md p-4" data-testid="sec-formandos">
            <h2 className="text-sm font-bold uppercase tracking-[0.12em] mb-3 flex items-center gap-2">
              <Users size={14} /> 10.3 Contratos Formandos
            </h2>
            <p className="text-xs text-[#6E6754] mb-2">
              <strong>{data.formandos.com_contrato}</strong> de <strong>{data.formandos.total}</strong> formandos
              já enviaram o contrato assinado. Cada contrato é gerido em cada ficha de inscrição.
            </p>
            <a href={`/admin/cursos/inscricoes?curso=${cursoId}`}
               className="inline-flex items-center gap-1 text-xs px-3 py-1.5 bg-[#0F1829] text-white rounded hover:bg-[#1A2540]"
               data-testid="link-formandos">
              Ver contratos individuais em Inscrições ↗
            </a>
          </section>
        </div>
      )}
    </div>
  );
}
